/**
 * `06/ Project Received` (Figma 12:2455) — `/countries/:cc/reports/received`, v19 D4.
 *
 * Documents by the day they CAME IN (`crfs.created_at`), in the window the server resolved
 * in the country's calendar. `reports.view` — the key the route enforces (`reports.controller
 * .ts`); Export is `reports.export`. The amount column is masked per row by the server and
 * rendered through `ServerAmount`; the CSV carries the same visible string.
 */

import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';

import { useCountryCode } from '../../auth/guards';
import { useSession } from '../../auth/session';
import { CRF_STAGES, WORK_TYPE_LABEL } from '../crfs/domain';
import { KpiTile } from '../dashboards/dataviz';
import { formatDateLong, formatMoney } from '../inquiries/format';
import { ServerAmount } from '../quotes/money';
import { fetchReceivedReport, reportKeys } from './api';
import type { ReceivedReportDto, ReceivedRowDto } from './api';
import { ReportFrame } from './ReportFrame';
import type { ReportColumn } from './ReportFrame';
import { useReportUrl } from './report-filters';

function stageLabel(status: string): string {
  return CRF_STAGES[status]?.label ?? status;
}

function columnsFor(countryCode: string): readonly ReportColumn<ReceivedRowDto>[] {
  return [
    {
      key: 'doc',
      header: 'Document',
      width: 'w-[170px]',
      cell: (row) => (
        <Link
          to={`/countries/${countryCode}/crfs/${row.crf_id}`}
          className="font-mono text-mono text-fg underline underline-offset-2 hover:decoration-2"
        >
          {row.doc_no}
        </Link>
      ),
      csv: (row) => row.doc_no,
    },
    { key: 'type', header: 'Work type', width: 'w-[90px]', cell: (row) => WORK_TYPE_LABEL[row.work_type], csv: (row) => WORK_TYPE_LABEL[row.work_type] },
    { key: 'party', header: 'Customer', cell: (row) => row.party.name, csv: (row) => row.party.name },
    {
      key: 'contact',
      header: 'Contact',
      cell: (row) =>
        row.contact === null || row.contact.name === null ? (
          <span className="text-fg-secondary">—</span>
        ) : (
          <span>
            {row.contact.name}
            {row.contact.phone === null ? '' : <span className="text-fg-secondary"> · {row.contact.phone}</span>}
          </span>
        ),
      csv: (row) => (row.contact?.name ?? '—') + (row.contact?.phone === null || row.contact?.phone === undefined ? '' : ` · ${row.contact.phone}`),
    },
    {
      key: 'stack',
      header: 'Platform / stack',
      cell: (row) => [...row.platforms, ...row.stacks].join(' / ') || <span className="text-fg-secondary">—</span>,
      csv: (row) => [...row.platforms, ...row.stacks].join(' / ') || '—',
    },
    { key: 'status', header: 'Stage', width: 'w-[150px]', cell: (row) => stageLabel(row.status), csv: (row) => stageLabel(row.status) },
    {
      key: 'amount',
      header: 'Amount',
      width: 'w-[130px]',
      numeric: true,
      cell: (row) => <ServerAmount value={row.amount} currency={row.currency} />,
      csv: (row) => formatMoney(row.amount, row.currency),
    },
    { key: 'received', header: 'Received', width: 'w-[120px]', cell: (row) => formatDateLong(row.received_at), csv: (row) => formatDateLong(row.received_at) },
    { key: 'owner', header: 'Owner', width: 'w-[150px]', cell: (row) => row.owner?.name ?? <span className="text-fg-secondary">—</span>, csv: (row) => row.owner?.name ?? '—' },
  ];
}

function Summary({ data }: { data: ReceivedReportDto }): React.JSX.Element {
  const nonZero = Object.entries(data.summary.by_status).filter(([, count]) => count > 0);
  return (
    <div className="grid grid-cols-2 gap-3 md:grid-cols-4" data-testid="received-summary">
      <KpiTile label="Received" value={data.summary.received} delta={data.summary.delta} deltaLabel="vs previous window" />
      <KpiTile label="Per week" value={data.summary.per_week === null ? '—' : String(data.summary.per_week)} detail={`${String(data.window.days)} days in the window`} />
      <div className="col-span-2 flex flex-col gap-1 rounded-md border border-line bg-surface px-4 py-3">
        <span className="text-body13 font-medium text-fg-secondary">By stage</span>
        {nonZero.length === 0 ? (
          <span className="text-body13 text-fg-secondary">No documents in this window.</span>
        ) : (
          <ul className="flex flex-wrap gap-x-4 gap-y-1">
            {nonZero.map(([status, count]) => (
              <li key={status} className="text-body13 text-fg">
                {stageLabel(status)} <span className="tnum font-medium">{count}</span>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}

export function ReceivedReportPage(): React.JSX.Element {
  const countryCode = useCountryCode();
  const { can } = useSession();
  const canView = can(countryCode, 'reports.view');
  const canExport = can(countryCode, 'reports.export');
  const { filters } = useReportUrl();

  const query = useQuery({
    queryKey: reportKeys.received(countryCode, filters),
    queryFn: ({ signal }) => fetchReceivedReport(countryCode, filters, signal),
    enabled: canView,
  });
  const columns = useMemo(() => columnsFor(countryCode), [countryCode]);

  return (
    <ReportFrame
      title="Project Received"
      testId="report-received"
      refusal={
        canView
          ? null
          : {
              title: 'Project Received is not available to this account',
              body: `Viewing reports in ${countryCode} needs the reports permission. Ask an administrator to widen your access.`,
            }
      }
      query={query}
      columns={columns}
      rowKey={(row) => row.crf_id}
      noun={['document', 'documents']}
      summary={(data) => <Summary data={data as ReceivedReportDto} />}
      canExport={canExport}
      filename={`${countryCode.toLowerCase()}-report-received.csv`}
      emptyTitle="Nothing was received in this window."
    />
  );
}
